/**
 * Which board a recruiter is currently viewing, remembered across a page reload.
 *
 * The selected board used to live only in React state, so any hard refresh threw it away and
 * snapped back to the viewer's OWN board — losing the colleague's board they had open (a jarring
 * "why am I back on my board?" every reload). We persist it in sessionStorage: a refresh lands back
 * where they were, while a genuinely fresh session (new tab, browser restart) still opens on their
 * own board, which is where people work most of the time.
 *
 * Keyed by the SIGNED-IN user's email, never the viewed board — so signing in as someone else on a
 * shared machine can't inherit the previous person's last view. The stored value is just an email
 * (an internal @finn.com colleague), not a credential.
 */
const storeKey = (myEmail: string) => `ta.board.selected:${myEmail.trim().toLowerCase()}`;

/** The board this user last viewed THIS session, or null when there is none / storage is blocked. */
export function readSelectedBoard(myEmail: string): string | null {
  if (!myEmail) return null;
  try {
    const value = sessionStorage.getItem(storeKey(myEmail));
    return value ? value.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

/** Remember the board this user is now viewing, so the next reload restores it. */
export function writeSelectedBoard(myEmail: string, board: string): void {
  if (!myEmail || !board) return;
  try {
    sessionStorage.setItem(storeKey(myEmail), board.trim().toLowerCase());
  } catch {
    // Storage full or blocked — the selection simply won't survive a refresh this session.
  }
}
